import React from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import FooterSection from '../home-landing/components/FooterSection';
import { useNavigate } from 'react-router-dom';



const ChapelHillLocation = () => {
  const navigate = useNavigate();

  const handleOrder = () => {
    window.open('https://online.skytab.com/5a689c513d0e59a5cb701768602d0256', '_blank');
  };

  const handleCall = () => {
    window.location.href = 'tel:+19849994537';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onCartClick={() => {}}
        onAccountClick={() => {}}
        onLogout={() => {}}
        onSearch={() => {}}
      />
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden mt-16">
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/images/image-1768968463180.png"
            alt="Chapel Hill location with vibrant neon wall art, food-themed decorations, and Quiero Mas Tequila neon sign"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>

        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center">

              <div className="inline-flex items-center space-x-2 bg-accent px-4 py-2 rounded-full mb-6">
                <Icon name="MapPin" size={20} className="text-accent-foreground" />
                <span className="text-accent-foreground font-body font-bold">CHAPEL HILL LOCATION</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 drop-shadow-2xl">
                Village Charm.
                <br />
                <span className="text-accent">Community Heart.</span>
              </h1>

              <p className="text-xl sm:text-2xl text-white/90 font-body mb-8 max-w-3xl mx-auto">
                Savor authentic Mexican flavors in the welcoming atmosphere of Meadowmont Village
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleOrder}
                  className="text-lg px-8 py-4 bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-lg shadow-2xl hover:scale-105 transition-all duration-300">
                  Order from This Location
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleCall}
                  className="text-lg px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-black font-bold rounded-lg shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
                  <Icon name="Phone" size={20} />
                  Make Reservation
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Neighborhood Description */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}>
              
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-6">
                The <span className="text-primary">Meadowmont Village</span>
              </h2>
              
              <p className="text-lg text-muted-foreground font-body mb-4">
                Nestled in the beautiful Meadowmont Village area, our Chapel Hill location offers a unique blend of upscale charm and welcoming community spirit.
              </p>
              
              <p className="text-lg text-muted-foreground font-body mb-4">
                This walkable mixed-use development features tree-lined streets and integrates shopping, dining, and residential areas seamlessly. Close to UNC Chapel Hill campus, the neighborhood attracts university faculty, families, and professionals seeking that perfect "village" atmosphere.
              </p>
              
              <p className="text-lg text-muted-foreground font-body mb-6">
                Experience the best of both worlds—the energy of college life combined with the warmth of an established community. It's the ideal setting to enjoy authentic Mexican cuisine with family and friends.
              </p>

              <div className="flex items-start space-x-3 bg-primary/10 p-4 rounded-lg">
                <Icon name="MapPin" size={24} className="text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-body font-bold text-foreground mb-1">Address</p>
                  <p className="text-muted-foreground">504 Meadowmont Village Cir, Chapel Hill, NC 27517</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative h-[400px] rounded-2xl overflow-hidden shadow-warm-xl">
              
              <Image
                src="/assets/images/image-1768968591896.png"
                alt="Kahlovera Tacos storefront exterior with outdoor seating area and red chairs in Meadowmont Village"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Location Features */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12">
            
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
              What Makes Us <span className="text-primary">Special</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'Home',
                title: 'Family-Friendly',
                description: 'Perfect atmosphere for gatherings and celebrations'
              },
              {
                icon: 'Users',
                title: 'Community Hub',
                description: 'Serving UNC faculty, families, and local professionals'
              },
              {
                icon: 'MapPin',
                title: 'Village Setting',
                description: 'Beautiful walkable neighborhood with charm'
              },
              {
                icon: 'Heart',
                title: 'Authentic Flavors',
                description: 'Traditional Mexican cuisine made with love'
              },
              {
                icon: 'Coffee',
                title: 'Relaxed Dining',
                description: 'Take your time and enjoy the experience'
              },
              {
                icon: 'Star',
                title: 'Established Favorite',
                description: 'A beloved cornerstone of the community'
              }
            ]?.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-card p-6 rounded-xl shadow-warm hover:shadow-warm-lg transition-all duration-300">
                
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={feature?.icon} size={24} className="text-primary" />
                </div>
                
                <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                  {feature?.title}
                </h3>
                
                <p className="text-muted-foreground font-body">
                  {feature?.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Call to Action */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}>
            
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
              Ready to Experience Chapel Hill's Flavor?
            </h2>
            
            <p className="text-xl mb-8 opacity-90">
              Order online or reserve your table today
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="outline"
                size="lg"
                onClick={handleOrder}
                className="text-lg px-8 py-4 bg-white text-primary hover:bg-gray-100 font-bold rounded-lg shadow-xl hover:scale-105 transition-all duration-300">
                Order Now
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={handleCall}
                className="text-lg px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-primary font-bold rounded-lg shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
                <Icon name="Phone" size={20} />
                Make Reservation
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Separator Line */}
      <div className="border-t border-secondary-foreground/10"></div>
      <FooterSection />
    </div>
  );
};

export default ChapelHillLocation;